import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { ScrollArea } from '../../components/ui/scroll-area';
import { formatDateIST, formatTimeIST } from '../../lib/dateUtils';
import {
  Loader2, Check, X, Pill, Clock, User, Mic, Pencil, ArrowRight,
  ChevronDown, ChevronUp, AlertTriangle, Sparkles
} from 'lucide-react';
import { VoiceNoteInput } from '../../components/ui/voice-note-input';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function DiffField({ label, current, proposed }) {
  const hasChange = current !== proposed;
  if (!hasChange && !proposed) return null;
  return (
    <div className="space-y-1">
      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</p>
      {hasChange && current && (
        <div className="text-[11px] text-slate-400 line-through bg-red-50/50 p-2 rounded-lg">{current}</div>
      )}
      {proposed && (
        <div className={`text-[11px] p-2 rounded-lg ${hasChange ? 'text-[#0E5132] bg-[#E6EFEA]/50 font-medium' : 'text-slate-600'}`}>{proposed}</div>
      )}
    </div>
  );
}

function DraftCard({ draft, onApprove, onReject }) {
  const [expanded, setExpanded] = useState(true);
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [processing, setProcessing] = useState(false);

  const hasChanges = Object.keys(draft.proposed || {}).some(
    k => draft.proposed[k] !== (draft.current?.[k] || '')
  );

  const handleApprove = async () => {
    setProcessing(true);
    try {
      await onApprove(draft.id);
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    setProcessing(true);
    try {
      await onReject(draft.id, rejectReason);
      setRejecting(false);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Card className={`border transition-colors ${draft.status === 'approved' ? 'border-emerald-200 bg-emerald-50/20' : draft.status === 'rejected' ? 'border-red-200 bg-red-50/20 opacity-60' : 'border-slate-200'}`} data-testid={`draft-${draft.id}`}>
      <CardHeader className="pb-2 pt-3 px-4 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-[10px] bg-[#E6EFEA] flex items-center justify-center">
              <Pill className="w-4 h-4 text-[#0E5132]" />
            </div>
            <div>
              <CardTitle className="text-sm font-heading">{draft.medicine_name || draft.sku}</CardTitle>
              <CardDescription className="text-[10px] flex items-center gap-2 mt-0.5">
                <span className="font-mono">{draft.sku}</span>
                <span>·</span>
                <User className="w-2.5 h-2.5" /> {draft.submitted_by} ({draft.submitted_by_role})
                <span>·</span>
                <Clock className="w-2.5 h-2.5" /> {formatDateIST(draft.created_at)} {formatTimeIST(draft.created_at)}
                {draft.source === 'audio' && <><span>·</span><Mic className="w-2.5 h-2.5 text-amber-500" /> Voice</>}
                {draft.source === 'ai_suggestion' && <><span>·</span><Sparkles className="w-2.5 h-2.5 text-violet-500" /> AI</>}
              </CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className={`text-[9px] ${draft.status === 'pending' ? 'border-amber-200 bg-amber-50 text-amber-700' : draft.status === 'approved' ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-red-200 bg-red-50 text-red-600'}`}>
              {draft.status}
            </Badge>
            {expanded ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}
          </div>
        </div>
      </CardHeader>
      {expanded && (
        <CardContent className="px-4 pb-4 space-y-3">
          {draft.transcript && (
            <div className="p-2 rounded-lg bg-amber-50/50 border border-amber-100">
              <p className="text-[9px] font-bold uppercase tracking-wider text-amber-600 mb-1">Voice Transcript</p>
              <p className="text-[11px] text-slate-600 italic">{draft.transcript}</p>
            </div>
          )}

          <div className="space-y-2">
            <DiffField label="Usage Notes" current={draft.current?.usage_notes} proposed={draft.proposed?.usage_notes} />
            <DiffField label="Indications" current={draft.current?.indications} proposed={draft.proposed?.indications} />
            <DiffField label="Contraindications" current={draft.current?.contraindications} proposed={draft.proposed?.contraindications} />
            <DiffField label="Preferred Combinations" current={draft.current?.preferred_combinations} proposed={draft.proposed?.preferred_combinations} />
            <DiffField label="Photosensitivity" current={draft.current?.photosensitivity} proposed={draft.proposed?.photosensitivity} />
            <DiffField label="Environmental Conditions" current={draft.current?.environmental_conditions} proposed={draft.proposed?.environmental_conditions} />
            <DiffField label="Skin Type Suitability" current={draft.current?.skin_type_suitability} proposed={draft.proposed?.skin_type_suitability} />
            <DiffField label="Application Timing" current={draft.current?.application_timing} proposed={draft.proposed?.application_timing} />
          </div>

          {!hasChanges && (
            <p className="text-[10px] text-muted-foreground italic">No changes from current enrichment</p>
          )}

          {draft.status === 'pending' && (
            <div className="flex items-center gap-2 pt-2 border-t">
              {rejecting ? (
                <div className="flex-1 space-y-2">
                  <Textarea
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="Reason for rejection (optional)"
                    className="text-xs min-h-[60px] rounded-[14px]"
                  />
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => setRejecting(false)} className="rounded-full text-xs">Cancel</Button>
                    <Button size="sm" onClick={handleReject} disabled={processing} className="rounded-full text-xs bg-red-600 hover:bg-red-700">
                      {processing ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <X className="w-3 h-3 mr-1" />} Reject
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <Button size="sm" onClick={handleApprove} disabled={processing} className="rounded-full text-xs bg-[#0E5132] hover:bg-[#063D2F]" data-testid={`approve-${draft.id}`}>
                    {processing ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Check className="w-3 h-3 mr-1" />} Approve
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setRejecting(true)} className="rounded-full text-xs" data-testid={`reject-${draft.id}`}>
                    <X className="w-3 h-3 mr-1" /> Reject
                  </Button>
                </>
              )}
            </div>
          )}

          {draft.status !== 'pending' && draft.reviewed_by && (
            <p className="text-[10px] text-muted-foreground pt-2 border-t">
              {draft.status === 'approved' ? 'Approved' : 'Rejected'} by {draft.reviewed_by}
              {draft.reviewed_at && ` · ${formatDateIST(draft.reviewed_at)}`}
            </p>
          )}
        </CardContent>
      )}
    </Card>
  );
}

export default function MedicineUpdatesPage() {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [enrichmentSuggestions, setEnrichmentSuggestions] = useState([]);

  const fetchDrafts = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/pharmacy/enrichment-drafts`, {
        params: { status_filter: filter },
        withCredentials: true,
      });
      setDrafts(data);
    } catch (err) {
      console.error('Failed to fetch drafts', err);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  const fetchEnrichmentSuggestions = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/knowledge-base/insights/enrichment-suggestions`, { withCredentials: true });
      setEnrichmentSuggestions(data.suggestions || []);
    } catch (err) {
      console.error('Failed to fetch enrichment suggestions', err);
    }
  }, []);

  useEffect(() => { fetchDrafts(); }, [fetchDrafts]);
  useEffect(() => { fetchEnrichmentSuggestions(); }, [fetchEnrichmentSuggestions]);

  const handleApprove = async (draftId) => {
    await axios.post(`${API_URL}/api/pharmacy/enrichment-drafts/${draftId}/approve`, {}, { withCredentials: true });
    toast.success('Enrichment approved and applied');
    fetchDrafts();
  };

  const handleReject = async (draftId, reason) => {
    await axios.post(`${API_URL}/api/pharmacy/enrichment-drafts/${draftId}/reject`, { reason }, { withCredentials: true });
    toast.success('Enrichment rejected');
    fetchDrafts();
  };

  const pendingCount = drafts.filter(d => d.status === 'pending').length;

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="medicine-updates-page">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold" style={{ color: '#1A2E22' }}>Medicine Updates</h1>
          <p className="text-sm mt-1" style={{ color: 'rgba(26,46,34,0.58)' }}>
            Review and approve medicine enrichment changes
          </p>
        </div>
        <div className="flex items-center gap-2">
          {['pending', 'approved', 'rejected', 'all'].map(f => (
            <Button
              key={f}
              variant={filter === f ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter(f)}
              className={`rounded-full text-xs capitalize ${filter === f ? 'bg-[#0E5132] hover:bg-[#063D2F]' : ''}`}
              data-testid={`filter-${f}`}
            >
              {f}
              {f === 'pending' && pendingCount > 0 && filter !== 'pending' && (
                <Badge className="ml-1.5 h-4 w-4 p-0 flex items-center justify-center text-[8px] bg-amber-500">{pendingCount}</Badge>
              )}
            </Button>
          ))}
        </div>
      </div>

      {/* Quick Voice Enrichment */}
      <Card className="border-blue-200 bg-blue-50/20">
        <CardHeader className="pb-2 pt-3 px-4">
          <CardTitle className="text-sm font-heading flex items-center gap-2 text-blue-700">
            <Mic className="w-4 h-4" />
            Quick Voice Enrichment
          </CardTitle>
          <CardDescription className="text-[10px]">Speak about any medicine to create an enrichment draft instantly</CardDescription>
        </CardHeader>
        <CardContent className="px-4 pb-3">
          <VoiceNoteInput
            endpoint="/api/pharmacy/voice-enrich-any"
            context={{}}
            onResult={() => { fetchDrafts(); }}
            label="Enrich by Voice"
          />
        </CardContent>
      </Card>

      {/* AI Enrichment Suggestions from Learning Loop */}
      {enrichmentSuggestions.length > 0 && (
        <Card className="border-violet-200 bg-violet-50/20">
          <CardHeader className="pb-2 pt-3 px-4">
            <CardTitle className="text-sm font-heading flex items-center gap-2 text-violet-700">
              <Sparkles className="w-4 h-4" />
              AI-Detected Patterns
              <Badge variant="outline" className="text-[9px] border-violet-200 text-violet-600">{enrichmentSuggestions.length}</Badge>
            </CardTitle>
            <CardDescription className="text-[10px]">Based on how doctors modify prescriptions</CardDescription>
          </CardHeader>
          <CardContent className="px-4 pb-3 space-y-2">
            {enrichmentSuggestions.map((s, i) => (
              <div key={i} className="p-3 rounded-lg border border-violet-100 bg-white">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium">{s.medicine_name} <span className="text-muted-foreground font-mono">({s.sku})</span></span>
                  <Badge variant="outline" className="text-[9px]">{s.occurrence_count}x seen</Badge>
                </div>
                <p className="text-[11px] text-violet-700">
                  <ArrowRight className="w-3 h-3 inline mr-1" />
                  Consider adding to {s.suggested_field}: "{s.suggested_note}"
                </p>
                {s.doctors?.length > 0 && (
                  <p className="text-[9px] text-muted-foreground mt-1">Suggested by: {s.doctors.join(', ')}</p>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Drafts List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-[#0E5132]" />
        </div>
      ) : drafts.length === 0 ? (
        <Card className="border-dashed border-2">
          <CardContent className="py-10 text-center">
            <Pill className="w-8 h-8 mx-auto mb-3" style={{ color: 'rgba(26,46,34,0.2)' }} />
            <p className="text-sm text-muted-foreground">
              {filter === 'pending' ? 'No pending enrichment updates' : `No ${filter} updates`}
            </p>
          </CardContent>
        </Card>
      ) : (
        <ScrollArea className="h-[600px]">
          <div className="space-y-3 pr-4">
            {drafts.map(draft => (
              <DraftCard key={draft.id} draft={draft} onApprove={handleApprove} onReject={handleReject} />
            ))}
          </div>
        </ScrollArea>
      )}
    </div>
  );
}
